import { FC, useEffect, useState } from "react";
import { Button, Divider, InputField, Slider } from "../../components";
import { useLoanApplication, useLoanCalculator } from "../../contexts";
import { useFormik } from "formik";
import {
  BuyNowPayLaterCalculatorFields,
  getBuyNowPayLaterLoanValidationSchema,
} from "./service";
import { LoanDurationType } from "../../hooks/api/types";
import { getFutureDate, roundTo } from "../../utils";
import { useStepper } from "../LoanApplicationSteps/Stepper";
import { ArrowLeftIcon } from "../../assets/icons";
import { useLoanMetrics } from "../../hooks";
import Transaction from "./Transaction";
import TermsAndConditions from "./TermsAndConditions";
import { useDpConfig } from "../../contexts/dpConfig";

type Props = {
  editMode?: boolean;
  handleSubmit?: () => void;
  handleCancel?: () => void;
};

const BuyNowPayLaterCalculator: FC<Props> = ({
  editMode = false,
  handleSubmit: handleSubmitFromParent,
  handleCancel: handleCancelFromParent,
}) => {
  const [isPartnerTermsAccepted, setIsPartnerTermsAccepted] =
    useState<boolean>();
  const {
    state: { buyNowPayLaterLoan, constraints },
    actions: { setBuyNowPayLaterLoan },
  } = useLoanCalculator();
  const {
    state: { dpTermsUrl },
  } = useDpConfig();
  const {
    state: { id },
  } = useLoanApplication();
  const { nextStep } = useStepper();
  const { totalPayment, buyNowPayLaterPaymentPerMonth } = useLoanMetrics();

  const { values, errors, setFieldValue, handleSubmit } =
    useFormik<BuyNowPayLaterCalculatorFields>({
      enableReinitialize: true,
      initialValues: {
        loanAmount:
          buyNowPayLaterLoan?.amount ||
          constraints.buyNowPayLaterLoan?.amount?.initial,
      },
      validationSchema: getBuyNowPayLaterLoanValidationSchema({
        loanAmount: {
          min: constraints.buyNowPayLaterLoan?.amount?.min,
          max: constraints.buyNowPayLaterLoan?.amount?.max,
          unit: constraints.buyNowPayLaterLoan?.amount?.unit ?? "AED",
        },
      }),
      onSubmit: async () => {
        nextStep();
        nextStep();
        nextStep();
      },
    });

  useEffect(() => {
    if (!id) {
      setBuyNowPayLaterLoan({
        ...buyNowPayLaterLoan,
        amount: values.loanAmount,
        duration: constraints.buyNowPayLaterLoan.duration.value,
        interestRate: constraints.buyNowPayLaterLoan.interestRate,
        durationType: constraints.buyNowPayLaterLoan.duration
          .unit as LoanDurationType,
      });
    }
  }, [id]);

  const handleInputChange = (name: string, value: string) => {
    const num = Number(value.replaceAll(",", ""));
    if (isNaN(num)) {
      return null;
    }
    setFieldValue(name, num, true);
    if (
      num > constraints.buyNowPayLaterLoan.amount.max ||
      num < constraints.buyNowPayLaterLoan.amount.min
    ) {
      return null;
    }
    return num;
  };

  const handleLoanAmountInputChange = (value: string) => {
    const val = handleInputChange("loanAmount", value);
    if (val !== null) {
      setBuyNowPayLaterLoan({
        ...buyNowPayLaterLoan,
        amount: val,
        duration: constraints.buyNowPayLaterLoan.duration.value,
        interestRate: constraints.buyNowPayLaterLoan.interestRate,
      });
    }
  };

  const handleLoanAmountSliderChange = (value: number) => {
    setFieldValue("loanAmount", value);
    setBuyNowPayLaterLoan({
      ...buyNowPayLaterLoan,
      amount: value,
      duration: constraints.buyNowPayLaterLoan.duration.value,
      interestRate: constraints.buyNowPayLaterLoan.interestRate,
    });
  };

  const generatePaymentSchedule = (): string[] => {
    const initialDate = getFutureDate(2, "days");
    const totalInstallments = buyNowPayLaterLoan.duration + 1;
    return Array.from({ length: totalInstallments }, (_, index) => {
      const date =
        index === 0
          ? initialDate
          : getFutureDate(1, "months", initialDate.plus({ months: index - 1 }));
      return date.toFormat("dd MMM yyyy");
    });
  };

  const handleTermsChange = (checked: boolean) => {
    setIsPartnerTermsAccepted(checked);
  };

  return (
    <div className="cx-w-full cx-flex cx-flex-col cx-p-10 cx-gap-10 cx-items-center cx-rounded-modal cx-border-0.5 cx-border-[#DDD] cx-shadow-modal cx-background-calculator-gradient">
      <div className="cx-flex cx-flex-col cx-items-center cx-w-full">
        <div className="cx-font-medium">Total repayment amount</div>
        <div className="cx-text-3xl cx-font-bold">
          {constraints.buyNowPayLaterLoan?.amount?.unit}{" "}
          {roundTo(totalPayment, 2).toLocaleString()}
        </div>
        <div className="cx-text-sm cx-text-text-secondary">
          Pay as low as{" "}
          {roundTo((buyNowPayLaterLoan.interestRate / 12) * 100, 2)}% monthly
          interest across {constraints.buyNowPayLaterLoan.duration.value + 1}{" "}
          installments*.
        </div>
      </div>
      <Divider className="cx-display-none" />
      <div className="cx-flex cx-flex-row cx-w-full cx-gap-6">
        <div className="cx-flex cx-flex-col cx-gap-4 cx-w-full">
          <div className="cx-font-semibold">Payment amount</div>
          <div>
            <InputField
              inputProps={{
                name: "loanAmount",
                value: values.loanAmount?.toLocaleString(),
                onChange: (e) => handleLoanAmountInputChange(e.target.value),
              }}
              inputClassName="cx-text-right !cx-p-2 cx-font-bold"
              prepend={constraints.buyNowPayLaterLoan?.amount?.unit}
              error={errors.loanAmount}
              hideErrorMessage
            />
          </div>
          <div className="">
            <Slider
              initial={values.loanAmount}
              min={constraints.buyNowPayLaterLoan?.amount?.min}
              max={constraints.buyNowPayLaterLoan?.amount?.max}
              unit={constraints.buyNowPayLaterLoan?.amount?.unit}
              value={values.loanAmount}
              label=""
              onChange={(value) => handleLoanAmountSliderChange(value)}
              renderThumb={false}
              interval={1000}
            />
          </div>
          {errors.loanAmount && (
            <div className="cx-text-xs cx-text-text-error">
              {errors.loanAmount}
            </div>
          )}
        </div>
      </div>
      <div className="cx-container cx-flex cx-gap-2">
        <p className="cx-text-base cx-text-black">Payment schedule</p>
      </div>
      <div className="cx-w-full">
        {generatePaymentSchedule().map((date, index) => (
          <Transaction
            key={index}
            installmentNo={index}
            isLast={index === buyNowPayLaterLoan.duration + 1}
            isFirst={index === 0}
            monthlyPayable={buyNowPayLaterPaymentPerMonth}
            currencyUnit={constraints.buyNowPayLaterLoan.amount.unit}
            date={date}
            isReviewScreen={false}
          />
        ))}
      </div>
      <div className="cx-w-[400px]">
        {editMode ? (
          <div className="cx-w-full cx-flex cx-flex-row cx-gap-x-4">
            <div className="cx-w-1/4">
              <Button
                label={<ArrowLeftIcon />}
                type="button"
                onClick={handleCancelFromParent}
                fullWidth
                outlined
              />
            </div>
            <div className="cx-w-3/4">
              <Button
                type="button"
                label="Confirm"
                fullWidth
                onClick={handleSubmitFromParent}
                disabled={Object.keys(errors).length > 0}
              />
            </div>
          </div>
        ) : (
          <Button
            label="Apply"
            arrow="right"
            fullWidth
            onClick={handleSubmit}
            disabled={
              Object.keys(errors).length > 0 ||
              (!isPartnerTermsAccepted && !!dpTermsUrl)
            }
          />
        )}
      </div>
      <Divider />
      <div className="cx-block">
        <TermsAndConditions onTermsChange={handleTermsChange} />
      </div>
    </div>
  );
};

export default BuyNowPayLaterCalculator;
